import React, { useEffect, useState, Fragment } from "react";
import { useDispatch } from "react-redux";
import { Row, Col, Card, Button, Table } from "react-bootstrap";
import { Fn_FillListData, Fn_GetReport } from "../../store/Functions";
import { API_WEB_URLS } from "../../constants/constAPI";
import PageTitle from "../layouts/PageTitle";
import jsPDF from "jspdf";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const currentYear = new Date().getFullYear();
const YEAR_LIST = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);

// Helper to format date into DD-MMM-YY (e.g., 01-Jan-05)
const formatDateDDMMMYY = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const monthNamesShort = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const month = monthNamesShort[d.getMonth()];
  const year = String(d.getFullYear()).slice(-2);
  return `${day}-${month}-${year}`;
};

// Helper to format date into DD/MM/YYYY
const formatDateDDMMYYYY = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear(); 
  return `${day}/${month}/${year}`;
};

const PfForm11NewJoining = () => {
  const dispatch = useDispatch();

  // Setup session state from localStorage
  const readSession = () => ({
    firm: (() => {
      try {
        return JSON.parse(localStorage.getItem("selectedFirm") || "null");
      } catch {
        return null;
      }
    })(),
    month: localStorage.getItem("selectedPayrollMonth") || "",
    year: parseInt(localStorage.getItem("selectedPayrollYear") || "0") || currentYear,
  });

  const [sessionFirm, setSessionFirm] = useState(() => readSession().firm);
  const [selectedMonth, setSelectedMonth] = useState(() => readSession().month);
  const [selectedYear, setSelectedYear] = useState(() => readSession().year);

  // Data lists & filters
  const [firmList, setFirmList] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPageIndex, setCurrentPageIndex] = useState(0);

  // Sync session changes from storage
  useEffect(() => {
    const onStorage = () => {
      const s = readSession();
      setSessionFirm(s.firm);
      setSelectedMonth(s.month);
      setSelectedYear(s.year);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Fetch initial base listings
  useEffect(() => {
    // Fetch firms list
    const firmApiUrl = `${API_WEB_URLS.MASTER}/0/token/${API_WEB_URLS.FirmMaster}/Id/0`;
    Fn_FillListData(dispatch, setFirmList, "gridData", firmApiUrl);
  }, [dispatch]);

  // Load report data when inputs change
  useEffect(() => {
    const firmId = sessionFirm?.Id || 0;
    if (firmId && selectedMonth && selectedYear) {
      loadReportData(firmId, selectedMonth, selectedYear);
    } else {
      setEmployees([]);
    }
  }, [sessionFirm, selectedMonth, selectedYear]);

  const loadReportData = async (firmId, monthName, year) => {
    setIsLoading(true);
    try {
      const monthNum = MONTH_NAMES.indexOf(monthName) + 1;
      const monthStr = String(monthNum);

      const fd = new FormData();
      fd.append("F_FirmMaster", firmId);
      fd.append("JoinMonth", monthStr);
      fd.append("JoinYear", year);

      const records = await Fn_GetReport(
        dispatch,
        () => {},
        "data",
        "GetPFForm11/0/token",
        { arguList: { id: 0, formData: fd } },
        true
      );

      let employeeData = [];
      if (records && records.length > 0) {
        const jsonString = records[0][""];
        if (jsonString) {
          employeeData = JSON.parse(jsonString);
        }
      }
      setEmployees(employeeData || []);
      setCurrentPageIndex(0);
    } catch (err) {
      console.error("Error loading employee data:", err);
      setEmployees([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Find Firm Code No. (which is PF No. on the branch) and Address
  const getFirmPfNo = () => {
    if (!sessionFirm || !sessionFirm.BranchData) return "N/A";
    try {
      const branches = JSON.parse(sessionFirm.BranchData);
      if (Array.isArray(branches) && branches.length > 0) {
        const mainBranch = branches.find(b => b.IsMainBranch === 1) || branches[0];
        return mainBranch.PFNo || "N/A";
      }
    } catch (e) {
      console.error("Error parsing branch data:", e);
    }
    return "N/A";
  };

  const getFirmAddress = () => {
    if (!sessionFirm || !sessionFirm.BranchData) return "N/A";
    try {
      const branches = JSON.parse(sessionFirm.BranchData);
      if (Array.isArray(branches) && branches.length > 0) {
        const mainBranch = branches.find(b => b.IsMainBranch === 1) || branches[0];
        return mainBranch.OfficeAddress || mainBranch.CoAddress || "N/A";
      }
    } catch (e) {
      console.error("Error parsing branch data:", e);
    }
    return "N/A";
  };

  // Filter employees whose name, father name, or PF No matches search term
  const filteredNewJoiners = employees.filter(emp => {
    const nameMatch = emp.Name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                      emp.FatherName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      (emp.PFNo && String(emp.PFNo).toLowerCase().includes(searchTerm.toLowerCase()));
                      
    return nameMatch;
  });

  // Reset page index if filters change the length
  useEffect(() => {
    if (currentPageIndex >= filteredNewJoiners.length && filteredNewJoiners.length > 0) {
      setCurrentPageIndex(0);
    }
  }, [filteredNewJoiners.length]);

  // PDF Multi-page generation
  const exportToPDF = () => {
    if (filteredNewJoiners.length === 0) {
      alert("No data available to export.");
      return;
    }

    const pdf = new jsPDF("p", "mm", "a4");

    filteredNewJoiners.forEach((emp, index) => {
      if (index > 0) pdf.addPage();

      pdf.setFontSize(8.5);
      pdf.setFont("Helvetica", "normal");
      pdf.text("Employees' Provident Fund Scheme, 1952 (Paragraph 34 & 57) & Employees Pension Scheme, 1995 (Paragraph 24)", 14, 10);
      pdf.setFontSize(9);
      pdf.text("Declaration by a person taking up employment in any establishment on which EPF Scheme, 1952 and/or EPS, 1995 is applicable.", 14, 14);
      
      pdf.setFont("Helvetica", "bold");
      pdf.setFontSize(10.5);
      pdf.text("New Form No. 11 - Declaration Form", 105, 20, { align: "center" });

      pdf.setFontSize(9);
      pdf.rect(14, 24, 182, 230); // Main outline box

      // Grid dividers
      pdf.line(14, 32, 196, 32);
      pdf.line(14, 40, 196, 40);
      pdf.line(14, 48, 196, 48);
      pdf.line(14, 56, 196, 56);
      pdf.line(14, 64, 196, 64);
      pdf.line(14, 72, 196, 72);
      pdf.line(14, 82, 196, 82);
      pdf.line(14, 90, 196, 90);
      pdf.line(14, 126, 196, 126);
      pdf.line(14, 150, 196, 150);
      pdf.line(14, 178, 196, 178);
      pdf.line(14, 218, 196, 218);

      // Left Labels & Right values
      pdf.text("1. Name of the Member", 16, 29);
      pdf.text(emp.Name?.toUpperCase() || "", 95, 29);

      pdf.text("2. Father's / Husband's Name", 16, 37);
      pdf.text(emp.FatherName?.toUpperCase() || "", 95, 37);

      pdf.text("3. Date of Birth", 16, 45);
      pdf.text(formatDateDDMMMYY(emp.DateOfBirth) || "", 95, 45);

      pdf.text("4. Gender: (Male/Female/Transgender)", 16, 53);
      pdf.text(emp.Sex?.toUpperCase() === "MALE" || emp.Sex?.toUpperCase() === "M" ? "Male" : emp.Sex?.toUpperCase() === "FEMALE" || emp.Sex?.toUpperCase() === "F" ? "Female" : emp.Sex || "", 95, 53);

      pdf.text("5. Marital Status: (Married/UnMarried/Widow/Divorcee)", 16, 61);
      pdf.text(emp.MaritalStatus || emp.WeeklyHoliday || "Un-Married", 95, 61); // Using Un-Married as default if marital status is blank

      pdf.text("6. A. Email ID:", 16, 69);
      pdf.text(emp.Email || emp.EmailID || "N/A", 45, 69);
      pdf.text("B. Mobile No.:", 110, 69);
      pdf.text(emp.MobileNo || "N/A", 145, 69);

      pdf.text("7. Whether earlier a member of Employee's Provident Fund Scheme, 1952", 16, 77);
      pdf.text(emp.OldMemberPF ? "True" : "False", 160, 77);

      pdf.text("8. Whether earlier a member of Employee's Pension Scheme, 1995", 16, 86);
      pdf.text(emp.OldMemberPension ? "True" : "False", 160, 86);

      // Section 9
      pdf.text("9. Previous employment details : [if Yes to 7 AND/OR 8 Above ]", 16, 94);
      pdf.text("a) Universal Account Number (UAN) :", 20, 100);
      pdf.text(emp.OldUAN || "N/A", 95, 100);
      pdf.text("b) Previous PF Account Number :", 20, 106);
      pdf.text(emp.PrevPFNo || "N/A", 95, 106);
      pdf.text("c) Date of exit from previous employment: (DD/MM/YYYY)", 20, 112);
      pdf.text(formatDateDDMMYYYY(emp.DateOfExitPr) || "N/A", 95, 112);
      pdf.text("d) Scheme Certificate No. (if issued):", 20, 118);
      pdf.text("N A", 95, 118);
      pdf.text("e) Pension Payment Order (PPO) No. (if issued):", 20, 124);
      pdf.text("N A", 95, 124);

      // Section 10
      pdf.text("10. a) International Worker :", 16, 131);
      pdf.text(emp.IsInternationalEmployee ? "True" : "False", 95, 131);
      pdf.text("b) If Yes, State Country of Origin (India/Name of Country) :", 20, 137);
      pdf.text(emp.CountryOrigin || "N/A", 115, 137);
      pdf.text("c) Passport No. :", 20, 143);
      pdf.text(emp.PassportNo || "N/A", 95, 143);
      pdf.text("d) Validity of Passport :", 20, 149);
      pdf.text(formatDateDDMMYYYY(emp.PassportTill) || "N/A", 95, 149);

      // Section 11
      pdf.text("11. KYC Details: (attach self attested copies of following KYCs)", 16, 155);
      pdf.text("a) Bank Account No. & IFSC Code :", 20, 161);
      pdf.text(`${emp.BankAccountNo || "N/A"} & ${emp.IFSCCode || "N/A"}`, 95, 161);
      pdf.text("b) AADHAR Number :", 20, 167);
      pdf.text(emp.AadharNumber || emp.AadharNo || "0", 95, 167);
      pdf.text("c) Permanent Account Number (PAN), if available :", 20, 173);
      pdf.text(emp.PanCardNo || "0", 95, 173);

      // Section 12 Undertaking
      pdf.text("UNDERTAKING", 16, 183);
      pdf.setFontSize(7.5);
      pdf.text("1) Certified that the particulars are true to the best of my knowledge.", 18, 188);
      pdf.text("2) I authorize EPFO to use my Aadhar for verification/authentication/eKYC purpose for the service delivery.", 18, 193);
      pdf.text("3) Kindly transfer the funds and service details, if applicable, from the previous PF account as declared above to the present", 18, 198);
      pdf.text("P.F. Account. (The transfer would be possible only if the identified KYC detail approved by previous employer has been", 18, 202);
      pdf.text("verified by present employer using his Digital Signature Certificate.)", 18, 206);
      pdf.text("4) In case of changes in above details, the same will be intimated to employer at the earliest.", 18, 211);
      
      pdf.setFontSize(9);
      pdf.text("Date:", 18, 216);
      pdf.text("Place:", 80, 216);
      pdf.text("Signature of Member", 150, 216);

      // Section 13 Employer Declaration
      pdf.text("DECLARATION BY PRESENT EMPLOYER", 16, 224);
      pdf.setFontSize(8);
      pdf.text(`A) The member Mr./Ms./Mrs. ${emp.Name?.toUpperCase() || ""} has joined on ${formatDateDDMMMYY(emp.PFJoinDate || emp.JoinDate)} and has been allotted`, 18, 229);
      pdf.text(`PF NO. ${getFirmPfNo()}/${emp.SerialNo || ""}`, 18, 233);
      pdf.text("B) In case the person was earlier not a member of EPF Scheme, 1952 and EPS, 1995 :", 18, 238);
      pdf.text(`* (Post allotment of UAN) the UAN allotted for the member is: ${emp.UAN || "N/A"}`, 22, 242);
      pdf.text("* Please Tick the Appropriate Option : The KYC details of the above member in the UAN database:", 22, 246);
      pdf.text("[ ] Have not been uploaded.     [ ] Have been uploaded but not approved.     [ ] Have been uploaded and approved with DSC.", 25, 250);

      pdf.text("C) In case the person was earlier a member of EPF Scheme, 1952 and EPF, 1995 :", 18, 256);
      pdf.text("* the above PF Account Number/UAN of the member as mentioned in (A) above has been tagged with his/her UAN/", 22, 260);
      pdf.text("Previous Member ID as declared by member.", 22, 264);
      pdf.text("* Please Tick the Appropriate Option :", 22, 268);
      pdf.text("[ ] The KYC details of the above member in the UAN database have been approved with Digital Signature Certificate", 25, 272);
      pdf.text("and transfer request has been generated on portal.", 27, 276);
      pdf.text("[ ] As the DSC of establishment are not registered with EPFO, the member has been informed to file physical claim", 25, 280);
      pdf.text("(Form-13) for transfer of funds from his previous establishment.", 27, 284);

      pdf.text("Date:", 18, 290);
      pdf.text("Signature of Employer with Seal of Establishment", 125, 290);

      // Page footer
      pdf.setFontSize(8);
      pdf.text(`-- ${index + 1} of ${filteredNewJoiners.length} --`, 105, 294, { align: "center" });
    });

    pdf.save(`PF_Form11_NewJoiners_${selectedMonth}_${selectedYear}.pdf`);
  };

  const printStyles = `
    @media print {
      body * {
        visibility: hidden !important;
      }
      #print-form11-container, #print-form11-container * {
        visibility: visible !important;
      }
      #print-form11-container {
        display: block !important;
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        background: #fff !important;
        color: #000 !important;
      }
      .form11-page-break {
        page-break-after: always !important;
        break-after: page !important;
        padding: 20px !important;
      }
      .no-print {
        display: none !important;
      }
      .form11-print-table {
        border-collapse: collapse !important;
        width: 100% !important;
        margin-top: 10px !important;
      }
      .form11-print-table td {
        border: 1px solid #000 !important;
        padding: 6px 10px !important;
        font-size: 11px !important;
        font-family: 'Courier New', Courier, monospace !important;
      }
    }
  `;

  const activeEmp = filteredNewJoiners[currentPageIndex];

  return (
    <Fragment>
      <style>{printStyles}</style>

      <div className="no-print">
        <PageTitle activeMenu="PF Form 11" motherMenu="Reports" pageContent="New Joining Declaration Form" />
      </div>

      {/* Filter Card */}
      <Card className="no-print mb-4 shadow-sm border-0" style={{ borderRadius: "10px", background: "#ffffff" }}>
        <Card.Body className="p-4">
          <Row className="align-items-end g-3">
            {/* Firm Dropdown */}
            <Col lg={4} md={6}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>
                Firm <span className="badge bg-light text-success ms-1 border" style={{ fontSize: "11px" }}>🔒 Header Locked</span>
              </label>
              <input
                type="text"
                className="form-control"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px", backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                value={sessionFirm?.Name || "Please select Firm from Header"}
                readOnly
                disabled
              />
            </Col>

            {/* Month Dropdown */}
            <Col lg={2} md={3}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>Month</label>
              <select
                className="form-select"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px" }}
                value={selectedMonth}
                onChange={(e) => {
                  setSelectedMonth(e.target.value);
                  if (e.target.value) localStorage.setItem("selectedPayrollMonth", e.target.value);
                  else localStorage.removeItem("selectedPayrollMonth");
                }}
              >
                <option value="">-- Month --</option>
                {MONTH_NAMES.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </Col>

            {/* Year Dropdown */}
            <Col lg={2} md={3}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>Year</label>
              <select
                className="form-select"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px" }}
                value={selectedYear}
                onChange={(e) => {
                  setSelectedYear(parseInt(e.target.value) || 0);
                  if (e.target.value) localStorage.setItem("selectedPayrollYear", e.target.value);
                  else localStorage.removeItem("selectedPayrollYear");
                }}
              >
                {YEAR_LIST.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </Col>

            {/* Search Filter */}
            <Col lg={4} md={12}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>Search Joiner</label>
              <input
                type="text"
                className="form-control"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px" }}
                placeholder="Search by name, father name, or PF no..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {!sessionFirm?.Id ? (
        <Card className="shadow-sm border-0" style={{ borderRadius: "10px" }}>
          <Card.Body className="text-center p-5">
            <h5 className="text-warning mb-2 fw-semibold">⚠ Session Setup Required</h5>
            <p className="text-muted mb-0 fs-13">
              Please select a **Firm**, **Month**, and **Year** from the filters above or from the **Dashboard Home** to load the report.
            </p>
          </Card.Body>
        </Card>
      ) : filteredNewJoiners.length === 0 ? (
        <Card className="shadow-sm border-0" style={{ borderRadius: "10px" }}>
          <Card.Body className="text-center p-5">
            <h5 className="text-muted mb-2 fw-semibold">No New Joiners Found</h5>
            <p className="text-muted mb-0 fs-13">
              There are no employees who joined the PF scheme during **{(selectedMonth || "").toUpperCase()} {selectedYear}** for this firm.
            </p>
          </Card.Body>
        </Card>
      ) : (
        <Fragment>
          {/* ─── WEB SLIDER PREVIEW (Premium Interactive Interface) ─── */}
          <div className="no-print">
            <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "10px", background: "#ffffff", overflow: "hidden" }}>
              <Card.Header className="d-flex justify-content-between align-items-center bg-white border-bottom py-3 px-4">
                <div>
                  <h5 className="mb-0 text-dark fw-bold fs-16" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    PF Form 11 (New Joining Declaration)
                  </h5>
                  <small className="text-muted fs-12">
                    Employee <strong>{currentPageIndex + 1}</strong> of <strong>{filteredNewJoiners.length}</strong> &nbsp;|&nbsp; <strong>{activeEmp?.Name?.toUpperCase()}</strong>
                  </small>
                </div>
                <div className="d-flex gap-2">
                  <Button variant="danger" size="sm" className="px-3" style={{ borderRadius: "5px", fontSize: "12px", background: "#E74C3C", border: "none" }} onClick={exportToPDF}>
                    <i className="fa fa-file-pdf-o me-2" />
                    Download All PDFs
                  </Button>
                  <Button variant="primary" size="sm" className="px-3 text-white" style={{ borderRadius: "5px", fontSize: "12px", background: "#2980B9", border: "none" }} onClick={() => window.print()}>
                    <i className="fa fa-print me-2" />
                    Print All Forms
                  </Button>
                </div>
              </Card.Header>

              <Card.Body className="p-4" style={{ fontFamily: "'Poppins', sans-serif" }}>
                {/* Navigation Toolbar */}
                <div className="d-flex justify-content-between align-items-center mb-4 p-2 bg-light rounded border">
                  <Button 
                    variant="outline-secondary" 
                    size="sm" 
                    disabled={currentPageIndex === 0} 
                    onClick={() => setCurrentPageIndex(prev => prev - 1)}
                  >
                    <i className="fa fa-chevron-left me-1" /> Previous
                  </Button>
                  
                  <span className="fw-semibold text-dark fs-13">
                    {activeEmp?.Name?.toUpperCase()} ({activeEmp?.CardNo || "Card No: N/A"})
                  </span>
                  
                  <Button 
                    variant="outline-secondary" 
                    size="sm" 
                    disabled={currentPageIndex === filteredNewJoiners.length - 1} 
                    onClick={() => setCurrentPageIndex(prev => prev + 1)}
                  >
                    Next <i className="fa fa-chevron-right ms-1" />
                  </Button>
                </div>

                {/* Form Representation Document Visualizer */}
                <div className="p-4 border rounded shadow-xs bg-white mx-auto" style={{ maxWidth: "800px", fontSize: "12px", lineHeight: "1.6", color: "#2C3E50" }}>
                  <div className="text-center mb-3">
                    <h6 className="fw-bold mb-1">New Form No. 11 - Declaration Form</h6>
                    <small className="text-muted">
                      Employees' Provident Fund Scheme, 1952 (Paragraph 34 & 57) & EPS, 1995 (Paragraph 24)
                    </small>
                  </div>

                  <Table bordered responsive size="sm" className="mb-3 text-dark align-middle">
                    <tbody>
                      <tr>
                        <td className="fw-bold" style={{ width: "40%" }}>1. Name of the Member</td>
                        <td>{activeEmp?.Name?.toUpperCase()}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold">2. Father's / Husband's Name</td>
                        <td>{activeEmp?.FatherName?.toUpperCase()}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold">3. Date of Birth</td>
                        <td>{formatDateDDMMMYY(activeEmp?.DateOfBirth)}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold">4. Gender</td>
                        <td>{activeEmp?.Sex}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold">5. Marital Status</td>
                        <td>Un-Married</td>
                      </tr>
                      <tr>
                        <td className="fw-bold">6. Contact Info</td>
                        <td>
                          <strong>Email:</strong> {activeEmp?.Email || activeEmp?.EmailID || "—"} <br />
                          <strong>Mobile:</strong> {activeEmp?.MobileNo || "—"}
                        </td>
                      </tr>
                      <tr>
                        <td className="fw-bold">7. Earlier Member of EPF Scheme, 1952?</td>
                        <td>{activeEmp?.OldMemberPF ? "Yes" : "No"}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold">8. Earlier Member of EPS Scheme, 1995?</td>
                        <td>{activeEmp?.OldMemberPension ? "Yes" : "No"}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold bg-light" colSpan={2}>9. Previous Employment Details (if applicable)</td>
                      </tr>
                      <tr>
                        <td className="ps-4">a) Universal Account Number (UAN)</td>
                        <td>{activeEmp?.OldUAN || "N/A"}</td>
                      </tr>
                      <tr>
                        <td className="ps-4">b) Previous Member ID / PF Account No.</td>
                        <td>{activeEmp?.PrevPFNo || "N/A"}</td>
                      </tr>
                      <tr>
                        <td className="ps-4">c) Date of Exit from Previous Employment</td>
                        <td>{formatDateDDMMYYYY(activeEmp?.DateOfExitPr) || "N/A"}</td>
                      </tr>
                      <tr>
                        <td className="ps-4">d) Scheme Certificate No. / e) PPO No.</td>
                        <td>N A</td>
                      </tr>
                      <tr>
                        <td className="fw-bold bg-light" colSpan={2}>10. International Worker Details</td>
                      </tr>
                      <tr>
                        <td className="ps-4">a) International Worker Status</td>
                        <td>{activeEmp?.IsInternationalEmployee ? "Yes" : "No"}</td>
                      </tr>
                      <tr>
                        <td className="ps-4">b) Country of Origin / c) Passport No.</td>
                        <td>{activeEmp?.CountryOrigin || "N/A"} &nbsp;|&nbsp; {activeEmp?.PassportNo || "N/A"}</td>
                      </tr>
                      <tr>
                        <td className="ps-4">d) Passport Validity Date</td>
                        <td>{formatDateDDMMYYYY(activeEmp?.PassportTill) || "N/A"}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold bg-light" colSpan={2}>11. KYC Details</td>
                      </tr>
                      <tr>
                        <td className="ps-4">a) Bank A/c No. & IFSC</td>
                        <td>{activeEmp?.BankAccountNo} & {activeEmp?.IFSCCode}</td>
                      </tr>
                      <tr>
                        <td className="ps-4">b) Aadhaar Card Number</td>
                        <td>{activeEmp?.AadharNumber || activeEmp?.AadharNo || "0"}</td>
                      </tr>
                      <tr>
                        <td className="ps-4">c) Permanent Account Number (PAN)</td>
                        <td>{activeEmp?.PanCardNo || "0"}</td>
                      </tr>
                    </tbody>
                  </Table>

                  <div className="p-3 bg-light rounded border mb-3">
                    <h6 className="fw-bold text-dark fs-12 mb-2">UNDERTAKING</h6>
                    <ol className="ps-3 mb-0" style={{ fontSize: "11px" }}>
                      <li>Certified that the particulars are true to the best of my knowledge.</li>
                      <li>I authorize EPFO to use my Aadhaar details for authentication purposes.</li>
                      <li>Kindly transfer funds from my previous PF account to the present PF account.</li>
                      <li>Any changes to details will be notified immediately.</li>
                    </ol>
                  </div>

                  <div className="border p-3 rounded bg-white">
                    <h6 className="fw-bold text-dark fs-12 mb-2">DECLARATION BY PRESENT EMPLOYER</h6>
                    <div style={{ fontSize: "11px" }}>
                      The member <strong>{activeEmp?.Name?.toUpperCase()}</strong> joined on <strong>{formatDateDDMMMYY(activeEmp?.PFJoinDate || activeEmp?.JoinDate)}</strong> and has been allotted PF No. <strong>{getFirmPfNo()}/{activeEmp?.SerialNo}</strong>.
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </div>

          {/* ─── PHYSICAL PRINTING VIEW (Conjoined Individual Form 11 Sheets) ─── */}
          <div id="print-form11-container" className="d-none">
            {filteredNewJoiners.map((emp, index) => (
              <div key={emp.Id} className="form11-page-break">
                <div style={{ textAlign: "center", fontSize: "10px", lineHeight: "1.3" }}>
                  Employees' Provident Fund Scheme, 1952 (Paragraph 34 & 57) & Employees Pension Scheme, 1995 (Paragraph 24)
                  <br />
                  Declaration by a person taking up employment in any establishment on which EPF Scheme, 1952 and/or EPS, 1995 is applicable.
                  <br />
                  <strong style={{ fontSize: "13px", display: "block", margin: "6px 0" }}>New Form No. 11 - Declaration Form</strong>
                </div>

                <table className="form11-print-table">
                  <tbody>
                    <tr>
                      <td style={{ width: "40%" }}><strong>1. Name of the Member</strong></td>
                      <td>{emp.Name?.toUpperCase()}</td>
                    </tr>
                    <tr>
                      <td><strong>2. Father's / Husband's Name</strong></td>
                      <td>{emp.FatherName?.toUpperCase()}</td>
                    </tr>
                    <tr>
                      <td><strong>3. Date of Birth</strong></td>
                      <td>{formatDateDDMMMYY(emp.DateOfBirth)}</td>
                    </tr>
                    <tr>
                      <td><strong>4. Gender</strong></td>
                      <td>{emp.Sex?.toUpperCase() === "MALE" || emp.Sex?.toUpperCase() === "M" ? "Male" : emp.Sex?.toUpperCase() === "FEMALE" || emp.Sex?.toUpperCase() === "F" ? "Female" : emp.Sex}</td>
                    </tr>
                    <tr>
                      <td><strong>5. Marital Status</strong></td>
                      <td>Un-Married</td>
                    </tr>
                    <tr>
                      <td><strong>6. Contact Info</strong></td>
                      <td>
                        <strong>Email:</strong> {emp.Email || emp.EmailID || "N/A"} &nbsp;|&nbsp; <strong>Mobile:</strong> {emp.MobileNo || "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <td><strong>7. PF Member Previously?</strong></td>
                      <td>{emp.OldMemberPF ? "True" : "False"}</td>
                    </tr>
                    <tr>
                      <td><strong>8. Pension Member Previously?</strong></td>
                      <td>{emp.OldMemberPension ? "True" : "False"}</td>
                    </tr>
                    <tr>
                      <td colSpan={2} style={{ background: "#eee", fontWeight: "bold" }}>9. Previous Employment Details</td>
                    </tr>
                    <tr>
                      <td style={{ paddingLeft: "25px" }}>a) Universal Account Number (UAN)</td>
                      <td>{emp.OldUAN || "N/A"}</td>
                    </tr>
                    <tr>
                      <td style={{ paddingLeft: "25px" }}>b) Previous PF Member ID</td>
                      <td>{emp.PrevPFNo || "N/A"}</td>
                    </tr>
                    <tr>
                      <td style={{ paddingLeft: "25px" }}>c) Date of Exit from Previous Establishment</td>
                      <td>{formatDateDDMMYYYY(emp.DateOfExitPr) || "N/A"}</td>
                    </tr>
                    <tr>
                      <td style={{ paddingLeft: "25px" }}>d) Scheme Certificate / e) PPO No.</td>
                      <td>N A</td>
                    </tr>
                    <tr>
                      <td colSpan={2} style={{ background: "#eee", fontWeight: "bold" }}>10. International Worker Details</td>
                    </tr>
                    <tr>
                      <td style={{ paddingLeft: "25px" }}>a) International Worker Status</td>
                      <td>{emp.IsInternationalEmployee ? "True" : "False"}</td>
                    </tr>
                    <tr>
                      <td style={{ paddingLeft: "25px" }}>b) Country of Origin / c) Passport No.</td>
                      <td>{emp.CountryOrigin || "N/A"} &nbsp;|&nbsp; {emp.PassportNo || "N/A"}</td>
                    </tr>
                    <tr>
                      <td style={{ paddingLeft: "25px" }}>d) Passport Expiry Date</td>
                      <td>{formatDateDDMMYYYY(emp.PassportTill) || "N/A"}</td>
                    </tr>
                    <tr>
                      <td colSpan={2} style={{ background: "#eee", fontWeight: "bold" }}>11. KYC Details</td>
                    </tr>
                    <tr>
                      <td style={{ paddingLeft: "25px" }}>a) Bank A/c No. & IFSC</td>
                      <td>{emp.BankAccountNo} & {emp.IFSCCode}</td>
                    </tr>
                    <tr>
                      <td style={{ paddingLeft: "25px" }}>b) Aadhaar Card Number</td>
                      <td>{emp.AadharNumber || emp.AadharNo || "0"}</td>
                    </tr>
                    <tr>
                      <td style={{ paddingLeft: "25px" }}>c) Permanent Account Number (PAN)</td>
                      <td>{emp.PanCardNo || "0"}</td>
                    </tr>
                  </tbody>
                </table>

                {/* Undertaking */}
                <div style={{ marginTop: "12px", border: "1px solid #000", padding: "8px", fontSize: "10px" }}>
                  <strong>UNDERTAKING</strong>
                  <div style={{ marginTop: "4px" }}>
                    1) Certified that the particulars are true to the best of my knowledge.
                    <br />
                    2) I authorize EPFO to use my Aadhaar for eKYC purpose.
                    <br />
                    3) Kindly transfer funds from my previous PF account to my present PF account.
                    <br />
                    4) Changes in details will be immediately intimated.
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: "10px" }}>
                    <div>Date: ________________</div>
                    <div>Place: ________________</div>
                    <div>Signature of Member: ________________</div>
                  </div>
                </div>

                {/* Present Employer Declaration */}
                <div style={{ marginTop: "12px", border: "1px solid #000", padding: "8px", fontSize: "10px" }}>
                  <strong>DECLARATION BY PRESENT EMPLOYER</strong>
                  <div style={{ marginTop: "4px" }}>
                    A) Mr./Ms./Mrs. <strong>{emp.Name?.toUpperCase()}</strong> joined on <strong>{formatDateDDMMMYY(emp.PFJoinDate || emp.JoinDate)}</strong> and has been allotted PF No. <strong>{getFirmPfNo()}/{emp.SerialNo}</strong>.
                    <br />
                    B) Post allotment of UAN, UAN is: <strong>{emp.UAN || "N/A"}</strong>. KYC details ticked as appropriate.
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: "10px" }}>
                    <div>Date: ________________</div>
                    <div>Signature of Employer with Seal: ________________</div>
                  </div>
                </div>

                <div style={{ textAlign: "center", fontSize: "10px", marginTop: "12px" }}>
                  -- Page {index + 1} of {filteredNewJoiners.length} --
                </div>
              </div>
            ))}
          </div>
        </Fragment>
      )}
    </Fragment>
  );
};

export default PfForm11NewJoining;
